import React, { useState, useEffect, useRef } from 'react';

interface Skill {
  id: number;
  name: string;
  job_listing_total_count: number;
}

interface SkillGroup {
  kind: string;
  kind_label: string;
  skills: Skill[];
}

interface SkillsFilterDropdownProps {
  show: boolean;
  onHide: () => void;
  onApply: (skillIds: number[]) => void;
  selectedSkillIds: number[];
  buttonRef: React.RefObject<HTMLButtonElement>;
}

const SkillsFilterDropdown: React.FC<SkillsFilterDropdownProps> = ({
  show,
  onHide,
  onApply,
  selectedSkillIds,
  buttonRef
}) => {
  const [skillGroups, setSkillGroups] = useState<SkillGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>(selectedSkillIds);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedIds(selectedSkillIds);
  }, [selectedSkillIds]);

  useEffect(() => {
    if (show) {
      fetchSkills();
    }
  }, [show]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        onHide();
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onHide, buttonRef]);

  const fetchSkills = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/skills');
      const data = await response.json();
      setSkillGroups(data);
    } catch (error) {
      console.error('Failed to fetch skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkillToggle = (skillId: number) => {
    const newSelection = selectedIds.includes(skillId) 
      ? selectedIds.filter(id => id !== skillId)
      : [...selectedIds, skillId];
    
    setSelectedIds(newSelection);
    
    // スキル選択時に自動的に検索を実行
    onApply(newSelection);
  };

  const handleApply = () => {
    onApply(selectedIds);
    onHide();
  };

  const handleClear = () => {
    setSelectedIds([]);
  };

  if (!show) return null;

  return (
    <div 
      ref={dropdownRef}
      className="position-absolute bg-white border rounded shadow-lg" 
      style={{ 
        top: '100%', 
        left: 0, 
        right: 0, 
        zIndex: 1000,
        maxHeight: '400px',
        overflowY: 'auto',
        minWidth: '500px'
      }}
    >
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">スキルで絞り込み</h6>
          <button type="button" className="btn-close" onClick={onHide}></button>
        </div>
        
        {loading ? (
          <div className="text-center">読み込み中...</div>
        ) : (
          <div className="accordion">
            {skillGroups.map((group, index) => (
              <div key={group.kind} className="accordion-item">
                <h2 className="accordion-header">
                  <button 
                    className="accordion-button collapsed" 
                    type="button" 
                    data-bs-toggle="collapse" 
                    data-bs-target={`#skill-accordion-${index}`}
                  >
                    {group.kind_label}
                  </button>
                </h2>
                <div id={`skill-accordion-${index}`} className="accordion-collapse collapse">
                  <div className="accordion-body">
                    <div className="row">
                      {group.skills.map(skill => (
                        <div key={skill.id} className="col-md-6 mb-2">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`skill-${skill.id}`}
                              checked={selectedIds.includes(skill.id)}
                              onChange={() => handleSkillToggle(skill.id)}
                            />
                            <label className="form-check-label" htmlFor={`skill-${skill.id}`}>
                              {skill.name} ({skill.job_listing_total_count})
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClear}>
            クリア
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleApply}>
            適用
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkillsFilterDropdown;